import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { PublicTokenStorageService } from '../auth/public-token-storage.service';

@Injectable()
export class PublicAuthInterceptor implements HttpInterceptor {
  private static readonly skippedSubscriptionAuthUrlParts = [
    '/Auth/request-code-prospect',
    '/Auth/request-code-client',
    '/Auth/validate-code',
    '/Auth/resume-session'
  ];

  private isRefreshing = false;
  private tokenSubject = new BehaviorSubject<any>(null);

  constructor(@Inject(PublicTokenStorageService) private tokenStorageService: PublicTokenStorageService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isAuthRequest(request.url) || this.isSubscriptionAuthRequest(request.url)) {
      return next.handle(this.removeAuthorizationHeader(request));
    }

    const accessToken = this.tokenStorageService.getToken();
    if (accessToken) {
      return next.handle(this.addTokenToRequest(request, accessToken)).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.requestToken(request, next);
          }
          return throwError(error);
        })
      );
    }

    return this.requestToken(request, next);
  }

  private requestToken(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);
      return this.tokenStorageService.refreshToken().pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;

          this.tokenSubject.next(token);
          return next.handle(this.addTokenToRequest(request, token));
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.tokenStorageService.clearToken();
          return throwError(error);
        })
      );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addTokenToRequest(request, token));
        })
      );
    }
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    if (!token?.trim()) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private removeAuthorizationHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!request.headers.has('Authorization')) {
      return request;
    }

    return request.clone({
      headers: request.headers.delete('Authorization')
    });
  }

  private isAuthRequest(url: string): boolean {
    return url.startsWith(environment.authServerUrl);
  }

  private isSubscriptionAuthRequest(url: string): boolean {
    const normalizedUrl = url.toLowerCase();

    return PublicAuthInterceptor.skippedSubscriptionAuthUrlParts.some(urlPart =>
      normalizedUrl.includes(urlPart.toLowerCase())
    );
  }
}
